import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import React, { useState, useEffect } from 'react';

import ConfirmableButton from '../../../components/ConfirmableButton';
import { useToast } from '../../../context/ToastContext';
import { LexOfficeRule } from '../apis/types';
import { useLexOfficeRuleset } from '../hooks/useLexOfficeRuleset';

import LexOfficeRulesDisplay from './LexOfficeRulesDisplay';
import LexOfficeRulesForm from './LexOfficeRulesForm';

const LexOfficeRulesets: React.FC = () => {
  const {
    ruleset,
    loading,
    saving,
    deleting,
    error,
    upsertRuleset,
    deleteRuleset,
  } = useLexOfficeRuleset();
  const { showToast } = useToast();

  const [isEditing, setIsEditing] = useState(false);
  const [customRule, setCustomRule] = useState('');
  const [rules, setRules] = useState<LexOfficeRule[]>([]);

  useEffect(() => {
    if (error) {
      showToast('Failed to load rulesets', error, 'error');
    }
  }, [error, showToast]);

  const startEditing = () => {
    setCustomRule(ruleset?.customRule || '');
    setRules(
      ruleset?.rules?.length
        ? [...ruleset.rules]
        : [{ condition: '', bookingCategoryId: '' }]
    );
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setCustomRule('');
    setRules([]);
  };

  const addRule = () => {
    setRules([...rules, { condition: '', bookingCategoryId: '' }]);
  };

  const removeRule = (index: number) => {
    setRules(rules.filter((_, i) => i !== index));
  };

  const updateRule = (
    index: number,
    field: keyof LexOfficeRule,
    value: string
  ) => {
    const updated = [...rules];
    updated[index] = { ...updated[index], [field]: value };
    setRules(updated);
  };

  const hasIncompleteRule = rules.some(
    r => !r.condition.trim() || !r.bookingCategoryId.trim()
  );
  const validRules = rules.filter(
    r => r.condition.trim() && r.bookingCategoryId.trim()
  );
  const canSubmit =
    !hasIncompleteRule &&
    (customRule.trim().length > 0 || validRules.length > 0);

  const handleSave = async () => {
    try {
      await upsertRuleset({
        customRule: customRule.trim() || undefined,
        rules: validRules,
      });
      showToast(
        ruleset ? 'Rules updated' : 'Rules created',
        'Lexware Office rules have been saved.',
        'success'
      );
      setIsEditing(false);
    } catch (err) {
      showToast(
        'Failed to save rules',
        err instanceof Error ? err.message : 'An unexpected error occurred',
        'error'
      );
    }
  };

  const handleDelete = async () => {
    try {
      await deleteRuleset();
      showToast(
        'Rules deleted',
        'Lexware Office rules have been removed.',
        'success'
      );
    } catch (err) {
      showToast(
        'Failed to delete rules',
        err instanceof Error ? err.message : 'An unexpected error occurred',
        'error'
      );
    }
  };

  if (loading) {
    return (
      <div className='bg-white dark:bg-gray-800 rounded-lg shadow-md p-6'>
        <div className='animate-pulse'>
          <div className='h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-4'></div>
          <div className='h-20 bg-gray-200 dark:bg-gray-700 rounded mb-2'></div>
        </div>
      </div>
    );
  }

  return (
    <div className='bg-white dark:bg-gray-800 rounded-lg shadow-md p-6'>
      <div className='flex justify-between items-center mb-4'>
        <h2 className='text-xl font-semibold text-gray-900 dark:text-white'>
          Lexware Office Rules
        </h2>
        <div className='flex gap-2'>
          {isEditing ? (
            <>
              <button
                type='button'
                onClick={cancelEditing}
                disabled={saving}
                className='px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors text-sm font-medium'
              >
                Cancel
              </button>
              <button
                type='button'
                onClick={handleSave}
                disabled={saving || !canSubmit}
                className='px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors text-sm font-medium'
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </>
          ) : ruleset ? (
            <>
              <button
                onClick={startEditing}
                className='p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900 rounded-md transition-colors'
                aria-label='Edit rules'
              >
                <PencilIcon className='w-5 h-5' />
              </button>
              <ConfirmableButton
                onConfirm={handleDelete}
                buttonClassName='p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900 rounded-md transition-colors'
                confirmButtonClassName='p-1 bg-white dark:bg-gray-800 rounded-full border border-gray-300 dark:border-gray-600 text-green-600 hover:text-green-800 hover:border-green-600'
                cancelButtonClassName='p-1 bg-white dark:bg-gray-800 rounded-full border border-gray-300 dark:border-gray-600 text-gray-600 hover:text-gray-800 hover:border-gray-600'
              >
                {deleting ? (
                  <span className='text-sm px-1'>Deleting...</span>
                ) : (
                  <TrashIcon className='w-5 h-5' />
                )}
              </ConfirmableButton>
            </>
          ) : (
            <button
              onClick={startEditing}
              className='px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium'
            >
              Add Rules
            </button>
          )}
        </div>
      </div>

      {isEditing ? (
        <LexOfficeRulesForm
          rules={rules}
          onAddRule={addRule}
          onRemoveRule={removeRule}
          onUpdateRule={updateRule}
          addRuleDisabled={hasIncompleteRule}
        />
      ) : (
        <LexOfficeRulesDisplay ruleset={ruleset} />
      )}
    </div>
  );
};

export default LexOfficeRulesets;
